import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useSWRConfig } from "swr";
import { toast } from "react-toastify";
import {
  UserConfigGetAPI,
  UserConfigUpdateAPI,
} from "@/features/accountSettings/api/UserConfigAPI";

/**
 * 地図初期位置を保存するためのフック
 * - /user/config を既存の更新APIで叩く
 * - 保存成功時は /user/config と /user/config/map を再検証する
 */
export const useSaveInitialMapPosition = (): {
  saveInitialMapPosition: (lat: number, lon: number) => Promise<void>;
  isSavingInitialMapPosition: boolean;
} => {
  const userConfig = UserConfigGetAPI();
  const { trigger, isMutating } = UserConfigUpdateAPI();
  const { mutate } = useSWRConfig();

  const saveInitialMapPosition = useCallback(
    async (lat: number, lon: number) => {
      if (!userConfig) {
        toast.error(
          "ユーザー設定を読み込み中です。少し待ってから再度お試しください。"
        );
        return;
      }

      const pictureRequestPastDays = userConfig.pictureRequestPastDays ?? 0;
      const mapChibanIsMatch = userConfig.mapChibanIsMatch ?? false;
      const mapZenrinChibanIncludesTbn =
        userConfig.mapZenrinChibanIncludesTbn ?? true;
      const mapPlotRequestDetailAdditionalFields =
        userConfig.mapPlotRequestDetailAdditionalFields;

      try {
        await trigger({
          pictureRequestPastDays,
          mapInitLat: lat,
          mapInitLon: lon,
          mapChibanIsMatch,
          mapZenrinChibanIncludesTbn,
          mapPlotRequestDetailAdditionalFields,
        });

        await Promise.all([mutate("/user/config"), mutate("/user/config/map")]);
        toast.success("地図初期表示を変更しました");
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast.error("地図初期表示の変更に失敗しました");
      }
    },
    [mutate, trigger, userConfig]
  );

  return {
    saveInitialMapPosition,
    isSavingInitialMapPosition: isMutating,
  };
};
